import { generatePath } from 'react-router-dom';
import { ApiRoutes } from 'routes/routeConstants/apiRoutes';
import { SortKeys } from 'enums/GraphDropdownEnums';
import { DataMappingProps } from 'enums/CreateProductStageNavigationBarEnums';
import { BreakDownDropdownOptionsEnums } from 'enums/BreakDownDropdownOptions';
import { ProductStagesEnums, ProductStagesValuesEnums } from 'enums/ProductStagesEnums';
import { ProductRawMaterial } from 'models/ProductRawMaterial/productRawMaterial.model';
import { ProductRawMaterialTransportations } from 'models/ProductRawMaterialTransportations/productRawMaterialTransportations.model';
import { CategoryOptions } from 'views/Dashboard';
import { ProductDistributionEmissionData } from 'models/ProductDistributionEmissionData/ProductDistributionEmissionData.model';
import { ProcessingFacilityEmissionData } from 'models/ProcessingFacilityEmissionData/ProcessingFacilityEmissionData.model';
import { EmissionEachStage } from 'models/EmissionEachStage/emissionEachStage.model';
import { DqrData, EmissionParams } from 'services/DashboardService/dashboard.service';
import { BreakdownDrowdownTypes } from '.';

export const getReadableKey = (key: string): string | undefined => {
  switch (key) {
    case 'productRawMaterial':
      return 'Raw Materials';
    case 'productTransportation':
      return 'Raw Materials Transportation';
    case 'processingFacility':
      return 'Processing Facility';
    case 'productDistribution':
      return 'Product Distribution and Storage';
    case 'total':
      return 'Total';
    default:
      return undefined;
  }
};

export const calculateCarbonValueOfEachStage = (
  emissionEachStage: EmissionEachStage | undefined,
  selectedType: boolean,
  productEmissionForLifeCycleSort: string
): CategoryOptions[] => {
  const options: { name: string | undefined; emission: number; key: string }[] =
    [];

  emissionEachStage &&
    (Object.keys(emissionEachStage) as Array<keyof EmissionEachStage>).forEach((item) => {
      if (!(item in emissionEachStage) || !emissionEachStage[item]) return;
      const emissionValue = Number(
        emissionEachStage[item]?.toFixed(selectedType ? 2 : 4)
      );
      options.push({
        name: getReadableKey(item as string),
        emission: emissionValue,
        key: item as string,
      });
    });

  const sorter =
    options &&
    options.sort((a, b) => {
      if (productEmissionForLifeCycleSort === SortKeys.DEFAULT) {
        return 0;
      } else if (productEmissionForLifeCycleSort === SortKeys.VALUE_LOW_TO_HIGH) {
        return Number(a.emission) - Number(b.emission);
      } else {
        return Number(b.emission) - Number(a.emission);
      }
    });

  return sorter;
};

export const downloadProductEmissionCSV = async (
  id: number,
  selectedBusinessModelValue: string,
  productEmissionForLifeCycleSort: string,
  downloadCSVForEachLifeCycle: (id: number, params: EmissionParams) => Promise<void>
) => {
  await downloadCSVForEachLifeCycle(id, {
    business_model: selectedBusinessModelValue,
    is_asc:
      productEmissionForLifeCycleSort === SortKeys.VALUE_LOW_TO_HIGH
        ? true
        : productEmissionForLifeCycleSort === SortKeys.VALUE_HIGH_TO_LOW
          ? false
          : undefined,
  });
};

export const downloadProductBreakdownEmissionCSV = (
  selectedStage: BreakdownDrowdownTypes,
  productId: number,
  productBreakdownEmissionForLifeCycleSort: string,
  downloadbreakdownGraphCSV: (url?: string, params?: {
    is_asc?: boolean;
  }) => Promise<void>
) => {
  let url;
  switch (selectedStage?.stage) {
    case DataMappingProps.STAGE_1:
      url = generatePath(ApiRoutes.FETCH_STAGE1_EMISSIONS, { id: productId });
      break;
    case DataMappingProps.STAGE_2:
      url = generatePath(ApiRoutes.FETCH_STAGE2_EMISSIONS, { id: productId });
      break;
    case DataMappingProps.STAGE_3:
      url = generatePath(ApiRoutes.FETCH_STAGE3_EMISSIONS, { id: productId });
      break;
    case DataMappingProps.STAGE_4:
      url = generatePath(ApiRoutes.FETCH_STAGE4_EMISSIONS, { id: productId });
      break;
    default:
      return;
  }
  downloadbreakdownGraphCSV(url, {
    is_asc:
      productBreakdownEmissionForLifeCycleSort === SortKeys.VALUE_LOW_TO_HIGH
        ? true
        : productBreakdownEmissionForLifeCycleSort === SortKeys.VALUE_HIGH_TO_LOW
          ? false
          : undefined,
  });
};

export const getCurrentStageName = (
  eachProductAnnualOptions: {
    label: string;
    value: string;
  }[],
  selectedStage: BreakdownDrowdownTypes
): string | undefined => {
  const currentStage = eachProductAnnualOptions.find(
    item => item.value === selectedStage.stage
  );
  return currentStage?.label;
};

export const convertToRawMaterialOptions = (
  data: ProductRawMaterialTransportations[],
  selectedStage: { type: string }
) => {
  const options: CategoryOptions[] = [];
  data?.forEach((item: ProductRawMaterial, index: number) => {
    options.push({
      name: item.activityLabel,
      emission:
        selectedStage?.type === BreakDownDropdownOptionsEnums.TOTAL
          ? Number(item.sumOfNormalisedCo2e) < 0
            ? 0
            : Number(item?.sumOfNormalisedCo2e?.toFixed(2))
          : selectedStage?.type === BreakDownDropdownOptionsEnums.STAGE
            ? Number(item.sumOfNormalisedCo2eStage) < 0
              ? 0
              : Number(item?.sumOfNormalisedCo2eStage?.toFixed(2))
            : Number(item.sumOfCo2e) < 0
              ? 0
              : Number(Number(item?.sumOfCo2e)?.toFixed(4)),
      key: item.id ?? index,
    });
  });
  return options;
};

export const convertToProcessingFacility = (
  data: ProcessingFacilityEmissionData, // while refactor will change this type
  selectedStage: { type: string }
) => {
  const options: CategoryOptions[] = [];
  options.push({
    name: data.processingFacilityName,
    emission:
      selectedStage?.type === BreakDownDropdownOptionsEnums.TOTAL
        ? Number(data.normalised_co2e) < 0
          ? 0
          : Number(data.normalised_co2e?.toFixed(2))
        : selectedStage?.type === BreakDownDropdownOptionsEnums.STAGE
          ? Number(data.normalisedCo2eStage) < 0
            ? 0
            : Number(data?.normalisedCo2eStage?.toFixed(2))
          : Number(data.co2e) < 0
            ? 0
            : Number(data?.co2e?.toFixed(4)),
    key: data.productId,
  });
  return options;
};

export const convertToDistributionAndStorage = (
  data: ProductDistributionEmissionData[],
  selectedStage: { type: string }
) => {
  const options: CategoryOptions[] = [];
  data?.forEach((item: any) => {
    options.push({
      name: item.end_market_location,
      emission:
        selectedStage?.type === BreakDownDropdownOptionsEnums.TOTAL
          ? Number(item.normalised_co2e) < 0
            ? 0
            : item.normalised_co2e?.toFixed(2)
          : selectedStage?.type === BreakDownDropdownOptionsEnums.STAGE
            ? Number(item.normalised_co2e_stage) < 0
              ? 0
              : item?.normalised_co2e_stage?.toFixed(2)
            : Number(item.co2e) < 0
              ? 0
              : Number(item?.co2e?.toFixed(4)),
      key: item.id,
    });
  });
  return options;
};

export const handleChangeCard = (
  values: ProductStagesEnums,
  setBusinessModelValue: (value: ProductStagesValuesEnums) => void
) => {
  if (values === ProductStagesEnums.B2C) {
    setBusinessModelValue(ProductStagesValuesEnums.B2C);
  }
  if (values === ProductStagesEnums.B2B) {
    setBusinessModelValue(ProductStagesValuesEnums.B2B);
  }
};

export const handleProductAnnualOptions = (
  value: string,
  setSelectedStage: React.Dispatch<React.SetStateAction<BreakdownDrowdownTypes>>
) => {
  setSelectedStage((prev) => prev && { ...prev, stage: value });
};

export const setRotation = (value:number) => {
  if (value >= 1.0 && value <= 3.0) {
    const startDegree = 45;
    const totalRange = 191;
    const minValue = 1.0;
    const maxValue = 3.0;

    const fraction = (value - minValue) / (maxValue - minValue);
    const degree = (startDegree - fraction * totalRange) % 360;

    return { degree, translateX: 41, translateY: -32.0041 };
  }
  return { degree: 0, translateX: 0, translateY: 0 };
};